import React, {
  useCallback,
  FocusEvent,
  ChangeEvent,
}                from 'react';
import styled    from 'styled-components';
import {
  FormikHelpers,
  FieldInputProps,
}                from 'formik';
import {
  TextField as MuiTextField,
  OutlinedTextFieldProps,
}                from '@material-ui/core';
import punyCode  from 'punycode';

interface ITextInputProps extends OutlinedTextFieldProps {
  form         : FormikHelpers<any>;
  field        : FieldInputProps<any>;
  stripChars   : string;
  isInvoice    : boolean;
  customLabel? : string;
  align?       : string;
  width?       : string;
}

const passFont = `
  @font-face {
    font-family : passFonto;
    font-style  : normal;
    font-weight : 400;
    src         : url(data:application/font-woff;charset=utf-8;base64,d09GRgABAAAAAATsAA8AAAAAB2QAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAABGRlRNAAABWAAAABwAAAAcg9+z70dERUYAAAF0AAAAHAAAAB4AJwANT1MvMgAAAZAAAAA/AAAAYH7AkBhjbWFwAAAB0AAAAFkAAAFqZowMx2N2dCAAAAIsAAAABAAAAAQAIgKIZ2FzcAAAAjAAAAAIAAAACAAAABBnbHlmAAACOAAAALkAAAE0MwNYJ2hlYWQAAAL0AAAAMAAAADYPA2KgaGhlYQAAAyQAAAAeAAAAJAU+ATJobXR4AAADRAAAABwAAAAcCPoA6mxvY2EAAANgAAAAEAAAABAA5gFMbWF4cAAAA3AAAAAaAAAAIAAKAE9uYW1lAAADjAAAARYAAAIgB4hZ03Bvc3QAAASkAAAAPgAAAE5Ojr8ld2ViZgAABOQAAAAGAAAABuK7WtIAAAABAAAAANXulPUAAAAA1viLwQAAAADW+JM4eNpjYGRgYOABYjEgZmJgBEI2IGYB8xgAA+AANXjaY2BifMg4gYGVgYVBAwOeYEAFjMgcp8yiFAYHBl7VP8wx/94wpDDHMIoo2DP8B8kx2TLHACkFBkYA8/IL3QB42mNgYGBmgGAZBkYGEEgB8hjBfBYGDyDNx8DBwMTABmTxMigoKKmeV/3z/z9YJTKf8f/X/4/vP7pldosLag4SYATqhgkyMgEJJnQFECcMOGChndEAfOwRuAAAAAAiAogAAQAB//8AD3jaY2BiUGJgYDRiWsXAzMDOoLeRkUHfZhM7C8Nbo41srHdsNjEzAZkMG5lBwqwg4U3sbIx/bDYxgsSNBRUF1Y0FlZUYBd6dOcO06m+YElMa0DiGJIZUxjuM9xjkGRhU2djZlJXU1UDQ1MTcDASNjcTFQFBUBGjYEkkVMJCU4gcCKRTeHCk+fn4+KSllsJiUJEhMUgrMUQbZk8bgz/iA8SRR9qzAY087FjEYD2QPDDAzMFgyAwC39TCRAAAAeNpjYGRgYADid/fqneL5bb4yyLMwgMC1H90HIfRkCxDN+IBpFZDiYGAC8QBbSwuceNpjYGRgYI7594aBgcmOAQgYHzAwMqACdgBbWQN0AAABdgAiAAAAAAAAAAABFAAAAj4AYgI+AGYB9AAAAAAAKgAqACoAKgBeAJIAmnjaY2BkYGBgZ1BgYGIAAUYGBNADEQAFQQBaAAB42o2PwUrDQBCGvzVV9GAQDx485exBY1CU3PQgVgIFI9prlVqDwcZNC/oSPoKP4HNUfQLfxYN/NytCe5GwO9/88+/MBAh5I8C0VoAtnYYNa8oaXpAn9RxIP/XcIqLreZENnjwvyfPieVVdXj2H7DHxPJH/2/M7sVn3/MGyOfb8SWjOGv4K2DRdctpkmtqhos+D6ISh4kiUUXDj1Fr3Bc/Oc0vPqec6A8aUyu1cdTaPZvyXyqz6Fm5axC7bxHOv/r/dnbSRXCk7+mpVrOqVtFqdp3NKxaHUgeod9cm40rtrzfrt2OyQa8fppCO9tk7d1x0rpiQcuDuRkjjtkHt16ctbuf/radZY52/PnEcphXpZOcofiEZNcQAAeNpjYGIAg///GBgZsAF2BgZGJkZmBmaGdkYWRla29JzKggxD9tK8TAMDAxc2D0MLU2NjENfI1M0ZACUXCrsAAAABWtLiugAA) format('woff');
  }
`;

const TextField = styled(MuiTextField)`
  && {
    width      : ${({ width }: any): any => width};
    margin-top : 15px;

    input {
      padding-right : ${({ customLabel }: any): any => customLabel && '27px'};
      color         : ${({ theme: { textColor } }): string => textColor.darkGray} !important;
      opacity       : 1 !important;
      font-size     : 16px;
      text-align    : ${({ align }: any): any => align};
    }
  }

  && input[type="password"]:not([value=""]) {
    font-family : passFonto, Roboto, Helvetica, Arial, sans-serif ;
    color       : ${({ theme: { textColor } }): string => textColor.secondary} !important;
  }

  ${passFont}
` as any;

const TextInputWrapper = styled.div`
  position: relative;

  > span {
    font-weight : 400;
    position    : absolute;
    right       : 10px;
    top         : 26px;
  }
`;

const getReplacedValue = (
  value          : string,
  filterCyrillic : RegExp | string,
  blankRegex     : RegExp,
  stripRegex     : RegExp,
): string => value
  .replace(blankRegex, '')
  .replace(stripRegex, '');

export const TextInput: React.FC<ITextInputProps> = ({
  form,
  field,
  stripChars,
  isInvoice,
  customLabel,
  align = 'left',
  width,
  ...props
}: ITextInputProps): JSX.Element => {
  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const blankRegex     = new RegExp('^[\\s]*$', 'g');
      const filterCyrillic = new RegExp('/[а-яА-ЯЁё-і]/', 'g');
      const stripRegex     = new RegExp(stripChars, 'g');
      const evt            = event;

      let value;

      if (isInvoice) {
        value = getReplacedValue(
          evt.target.value,
          filterCyrillic,
          blankRegex,
          stripRegex,
        );
      } else {
        value = getReplacedValue(
          punyCode.toUnicode(evt.target.value),
          filterCyrillic,
          blankRegex,
          stripRegex,
        );
      }

      form.setFieldTouched(field.name, true, false);

      evt.target.value = value;

      field.onChange(evt);
    },
    [stripChars, isInvoice, form, field],
  );

  const onBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>): void => {
      const evt   = event;
      const value = evt.target.value.trim();

      form.setFieldValue(field.name, value);

      evt.target.value = value;
      field.onBlur(evt);
    },
    [form, field],
  );

  return (
    <TextInputWrapper>
      <TextField
        {...field}
        {...props}
        onChange    = {onChange}
        onBlur      = {onBlur}
        align       = {align}
        width       = {width}
      />
      <span>{customLabel}</span>
    </TextInputWrapper>

  );
};

TextInput.defaultProps = {
  variant    : 'outlined',
  margin     : 'dense',
  fullWidth  : true,
  stripChars : '',
};
